/**
 * Sprint v1.6 Stufe 4D (Do 03.09.2026) — Stressmuster-Grid.
 *
 * 3-Tile-Grid als Selbstreflexions-Werkzeug in Phase 1 "Mein Muster".
 * Zeigt drei Reaktionsmuster unter Druck / im Konflikt:
 *   - Zugang erzwingen (Push): Argument, Nachhaken, Rechthaben
 *   - Rückzug (Withdraw):      Schweigen, Distanz, Vermeidung
 *   - Resignation (Give-up):   Aufgeben, Klein-Machen, "egal"
 *
 * State-Persistence: Aktuelle Auswahl bleibt in sessionStorage
 * (Key: f4s_stressmuster_selected). Post-Launch W1 optional in DB.
 *
 * Sprint-Auftrag Do 03.09. 11:30 · Item 1 · Option Gamma · Pre-Launch-Scope.
 */
import React, { useEffect, useState } from "react";
import { Flame, EyeOff, CloudRain } from "lucide-react";

const STORAGE_KEY = "f4s_stressmuster_selected";

const MUSTER = [
  {
    id: "zugang-erzwingen",
    label: "Zugang erzwingen",
    icon: Flame,
    hint: "Argumentieren. Nachhaken. Rechthaben wollen.",
    beschreibung:
      "Wenn du merkst: Ich pushe stärker, damit die Tür aufgeht. Du wirst lauter, hartnäckiger, argumentativer — und die Tür schließt sich noch weiter.",
  },
  {
    id: "rueckzug",
    label: "Rückzug",
    icon: EyeOff,
    hint: "Schweigen. Distanz. Vermeidung.",
    beschreibung:
      "Wenn du merkst: Ich mach zu. Kein Wort mehr, kein Blick, kein Kontakt — bis das Thema von selbst wegkühlt oder eskaliert.",
  },
  {
    id: "resignation",
    label: "Resignation",
    icon: CloudRain,
    hint: "Aufgeben. Klein machen. „Egal.\u201c",
    beschreibung:
      "Wenn du merkst: Ich mache mich klein. „Ist eh egal.\u201c „Bringt eh nichts.\u201c — und die Tür bleibt zu, weil du gar nicht mehr klopfst.",
  },
];

export default function StressmusterGrid() {
  const [selected, setSelected] = useState(null);

  // Persistenz: Session-Storage laden
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && MUSTER.some((m) => m.id === stored)) {
        setSelected(stored);
      }
    } catch (_) {
      /* sessionStorage nicht verfügbar → no-op */
    }
  }, []);

  const handleSelect = (id) => {
    const next = selected === id ? null : id;
    setSelected(next);
    try {
      if (next) sessionStorage.setItem(STORAGE_KEY, next);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* no-op */
    }
  };

  const activeMuster = MUSTER.find((m) => m.id === selected);

  return (
    <section
      className="space-y-4"
      data-testid="stressmuster-grid"
      aria-label="Stressmuster erkennen"
    >
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        data-testid="stressmuster-grid-tiles"
      >
        {MUSTER.map((m) => {
          const Icon = m.icon;
          const isActive = selected === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => handleSelect(m.id)}
              data-testid={`stressmuster-tile-${m.id}`}
              data-active={isActive ? "true" : "false"}
              aria-pressed={isActive}
              className={[
                "text-left rounded-2xl border p-4 transition-all",
                "focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]",
                isActive
                  ? "bg-[var(--fts-primary-bright)] border-[var(--fts-primary-bright)] text-white shadow-md"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-[var(--fts-primary-bright)] hover:shadow-sm",
              ].join(" ")}
            >
              <div className="flex items-start gap-3">
                <div
                  className={[
                    "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0",
                    isActive
                      ? "bg-white/15"
                      : "bg-[var(--fts-primary-bright)]/10 text-[var(--fts-primary-bright)]",
                  ].join(" ")}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-sm font-extrabold leading-tight"
                    data-testid={`stressmuster-tile-${m.id}-label`}
                  >
                    {m.label}
                  </p>
                  <p
                    className={[
                      "mt-1 text-xs leading-snug",
                      isActive ? "text-white/90" : "text-slate-500 dark:text-slate-400",
                    ].join(" ")}
                  >
                    {m.hint}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {activeMuster && (
        <div
          className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border-l-4 border-[var(--fts-primary-bright)] px-4 py-3 text-sm text-slate-800 dark:text-slate-200 leading-relaxed"
          data-testid="stressmuster-detail"
        >
          <p className="font-bold text-slate-900 dark:text-white mb-1">{activeMuster.label}</p>
          <p>{activeMuster.beschreibung}</p>
        </div>
      )}
    </section>
  );
}
